import { useEffect, useState } from 'react';
import facilityCard1 from './assets/news/news3.jpg';
import facilityCard2 from './assets/news/news5.jpg';
import facilityCard3 from './assets/news/news6.jpg';
import facilityCard4 from './assets/news/news8.jpg';

function TechnologyDetailPage({ technologyId, onNavigate }) {
  const [activeImageSlide, setActiveImageSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [technologyId]);

  const technologiesData = {
    'smart-classrooms': {
      title: 'Smart Classrooms',
      subtitle: 'Interactive Learning Environments',
      description: 'Classrooms equipped with cutting-edge technology to create engaging, personalized learning experiences that inspire curiosity and collaboration.',
      color: '#3b82f6',
      colorLight: '#dbeafe',
      icon: 'fa-chalkboard',
      images: [facilityCard1, facilityCard2, facilityCard3],
      features: [
        {
          icon: 'fa-tv',
          title: 'Interactive Displays',
          detail: 'Large touchscreen boards for dynamic presentations and real-time collaboration'
        },
        {
          icon: 'fa-wifi',
          title: 'High-Speed Connectivity',
          detail: 'Reliable wireless network supporting seamless digital learning experiences'
        },
        {
          icon: 'fa-laptop',
          title: 'Device Integration',
          detail: 'Seamless connection for student devices, enabling BYOD learning models'
        },
        {
          icon: 'fa-cloud',
          title: 'Cloud-Based Resources',
          detail: 'Access to digital libraries, educational apps, and collaborative platforms'
        }
      ],
      highlights: [
        'Real-time student engagement tracking',
        'Multimedia content delivery capabilities',
        'Flexible furniture for collaborative learning',
        'Teacher training and technical support included',
        'Accessibility features for all learners'
      ],
      content: 'Smart Classrooms represent the future of education. These spaces integrate technology seamlessly into the learning environment, allowing teachers to create dynamic, interactive lessons that cater to diverse learning styles. Students benefit from multimedia content, real-time feedback, and opportunities for digital collaboration. The technology supports both traditional instruction and innovative pedagogies like flipped classrooms and project-based learning. Teachers receive comprehensive training to maximize the potential of these tools, ensuring technology enhances rather than distracts from learning objectives.',
      benefits: [
        'Enhanced student engagement and participation',
        'Personalized learning pathways',
        'Real-time assessment and feedback',
        'Increased collaboration opportunities',
        'Preparation for digital-age careers'
      ]
    },
    'it-lab': {
      title: 'IT Lab',
      subtitle: 'Computing & Digital Literacy Hub',
      description: 'A dedicated space where students develop computational thinking, coding skills, and digital literacy essential for the modern world.',
      color: '#10b981',
      colorLight: '#d1fae5',
      icon: 'fa-code',
      images: [facilityCard2, facilityCard3, facilityCard4],
      features: [
        {
          icon: 'fa-desktop',
          title: 'Modern Workstations',
          detail: 'High-performance computers with latest software and development tools'
        },
        {
          icon: 'fa-graduation-cap',
          title: 'Coding Platforms',
          detail: 'Access to industry-standard programming languages and learning environments'
        },
        {
          icon: 'fa-network-wired',
          title: 'Network Infrastructure',
          detail: 'Secure, fast network for collaborative projects and online learning'
        },
        {
          icon: 'fa-book-open',
          title: 'Learning Resources',
          detail: 'Comprehensive tutorials, documentation, and project templates'
        }
      ],
      highlights: [
        'Capacity: 30 students per session',
        'Multiple programming languages supported',
        'Project-based learning approach',
        'Industry-standard tools and software',
        'Regular workshops and coding competitions'
      ],
      content: 'The IT Lab is where digital natives become digital creators. Students learn to code, understand algorithms, and develop problem-solving skills through hands-on projects. The lab supports various programming languages and frameworks, from beginner-friendly platforms like Scratch to professional tools like Python and JavaScript. Students work on real-world projects, participate in coding competitions, and build portfolios that prepare them for tech careers. The collaborative environment encourages peer learning and mentorship, fostering a community of young innovators.',
      benefits: [
        'Computational thinking development',
        'Career readiness in tech fields',
        'Problem-solving and logic skills',
        'Digital citizenship awareness',
        'Entrepreneurial mindset cultivation'
      ]
    },
    'maker-space': {
      title: 'Maker Space',
      subtitle: 'Innovation & Design Studio',
      description: 'A creative hub where students transform ideas into reality through hands-on making, prototyping, and collaborative design thinking.',
      color: '#f59e0b',
      colorLight: '#fef3c7',
      icon: 'fa-hammer',
      images: [facilityCard3, facilityCard1, facilityCard2],
      features: [
        {
          icon: 'fa-cube',
          title: '3D Printing & Design',
          detail: 'CAD software and 3D printers for rapid prototyping and design exploration'
        },
        {
          icon: 'fa-microchip',
          title: 'Electronics & Robotics',
          detail: 'Microcontrollers, sensors, and robotics kits for STEM projects'
        },
        {
          icon: 'fa-tools',
          title: 'Hand & Power Tools',
          detail: 'Safe, supervised access to tools for woodworking, metalworking, and crafting'
        },
        {
          icon: 'fa-lightbulb',
          title: 'Design Thinking',
          detail: 'Structured methodology for innovation and creative problem-solving'
        }
      ],
      highlights: [
        'Open-ended project opportunities',
        'Interdisciplinary collaboration',
        'Safety training and supervision',
        'Access to diverse materials and tools',
        'Showcase opportunities for student work'
      ],
      content: 'The Maker Space embodies the spirit of innovation and creativity. It\'s a place where students from different disciplines come together to design, build, and iterate. Whether creating a robot, designing a product, or building an art installation, students learn through doing. The space encourages experimentation, celebrates failure as part of learning, and fosters a growth mindset. With access to 3D printers, electronics, traditional tools, and materials, students have the resources to bring any idea to life. Regular workshops, maker fairs, and project showcases celebrate student creativity and innovation.',
      benefits: [
        'Creative expression and innovation',
        'Hands-on STEM learning',
        'Entrepreneurial skills development',
        'Interdisciplinary thinking',
        'Confidence in making and building'
      ]
    }
  };

  const technology = technologiesData[technologyId] || technologiesData['smart-classrooms'];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % technology.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [technology.images.length]);

  return (
    <section className="technology-detail-page">
      {/* Hero Section */}
      <section className="technology-detail-hero" style={{ backgroundColor: technology.color }}>
        <div className="technology-detail-hero-pattern" />
        <div className="technology-detail-hero-image">
          <div className="technology-detail-hero-media" aria-hidden="true">
            {technology.images.map((image, index) => (
              <div
                key={`${technologyId}-${index}`}
                className={`technology-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
              >
                <img src={image} alt={technology.title} />
              </div>
            ))}
          </div>
          <div className="technology-detail-hero-overlay" aria-hidden="true" />
        </div>
        <div className="technology-detail-hero-content">
          <button
            className="technology-detail-back-btn"
            onClick={() => onNavigate && onNavigate('facilities')}
            aria-label="Back to facilities"
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="technology-detail-hero-icon">
            <i className={`fas ${technology.icon}`} />
          </div>
          <h1 className="technology-detail-hero-title">{technology.title}</h1>
          <p className="technology-detail-hero-subtitle">{technology.subtitle}</p>
        </div>
        <div className="technology-detail-hero-dots">
          {technology.images.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              className={`technology-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
              onClick={() => setActiveImageSlide(index)}
              aria-label={`Show slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="technology-detail-main">
        {/* About Section */}
        <div className="technology-detail-section">
          <div className="technology-detail-section-header">
            <h2>About {technology.title}</h2>
            <div className="technology-detail-section-line" style={{ backgroundColor: technology.color }} />
          </div>
          <p className="technology-detail-description">{technology.description}</p>
          <div className="technology-detail-highlight-box" style={{ backgroundColor: technology.colorLight, borderLeftColor: technology.color }}>
            <i className={`fas ${technology.icon}`} style={{ color: technology.color }} />
            <p>{technology.content}</p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="technology-detail-section">
          <div className="technology-detail-section-header">
            <h2>Key Features</h2>
            <div className="technology-detail-section-line" style={{ backgroundColor: technology.color }} />
          </div>
          <div className="technology-features-grid">
            {technology.features.map((feature, index) => (
              <div key={`feature-${index}`} className="technology-feature-card" style={{ borderTopColor: technology.color }}>
                <div 
                  className="technology-feature-icon" 
                  style={{ 
                    '--feature-color': technology.color,
                    '--feature-color-dark': technology.color + 'cc'
                  }}
                >
                  <i className={`fas ${feature.icon}`} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="technology-detail-section">
          <div className="technology-detail-section-header">
            <h2>Learning Benefits</h2>
            <div className="technology-detail-section-line" style={{ backgroundColor: technology.color }} />
          </div>
          <div className="technology-benefits-grid">
            {technology.benefits.map((benefit, index) => (
              <div key={`benefit-${index}`} className="technology-benefit-card" style={{ backgroundColor: technology.colorLight, borderLeftColor: technology.color }}>
                <div className="technology-benefit-icon" style={{ color: technology.color }}>
                  <i className="fas fa-star" />
                </div>
                <p>{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="technology-detail-section">
          <div className="technology-detail-section-header">
            <h2>Highlights & Specifications</h2>
            <div className="technology-detail-section-line" style={{ backgroundColor: technology.color }} />
          </div>
          <div className="technology-highlights-grid">
            {technology.highlights.map((highlight, index) => (
              <div key={`highlight-${index}`} className="technology-highlight-card" style={{ backgroundColor: technology.colorLight, borderLeftColor: technology.color }}>
                <div className="technology-highlight-icon" style={{ color: technology.color }}>
                  <i className="fas fa-check-circle" />
                </div>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="technology-detail-cta" 
        style={{ 
          '--cta-color': technology.color,
          '--cta-color-dark': technology.color + 'cc'
        }}
      >
        <div className="technology-detail-cta-content">
          <h2>Ready to Experience {technology.title}?</h2>
          <p>Schedule a campus tour or learn more about our technology programs.</p>
          <button className="technology-detail-cta-btn" onClick={() => onNavigate && onNavigate('contact')}>
            Schedule a Tour <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default TechnologyDetailPage;
